import { useAuth0 } from '@auth0/auth0-react';
import { useDispatch, useSelector } from 'react-redux';
import { WidgetID } from '../../../models/widget.model';
import {
  parseDataTypeResult,
  toDataTypeResult,
  useCreateUserDataRowMutation,
  useGetUserDataListQuery,
  useUpdateUserDataRowMutation,
} from '../../../store/api/user-data.api';

import {
  loadConfig,
  selectConfigKey,
  selectGridConfig,
  setGridConfigKey,
} from '../../../store/slices/grid-config.slice';
import { useCallback, useEffect, useMemo } from 'react';
import { UserDataType } from '../../../models/user.model';
import { GridConfigDictionary } from '../../../models/grid-config.model';
import { useModuleProjectContext } from 'common/contexts/useModuleProjectContext';
import { showToast } from 'common/store/slices/notification.slice';
import { NotificationType } from 'common/models/notification.model';

export function useLoadGridConfigData() {
  const dispatch = useDispatch();
  const { isAuthenticated } = useAuth0();
  const { activeProject } = useModuleProjectContext();
  const { data, isLoading, isFetching, isUninitialized } = useGetUserDataListQuery(
    { type: UserDataType.GridConfiguration, projectId: activeProject?.moduleProjectID },
    {
      skip: !isAuthenticated || !activeProject?.moduleProjectID,
      refetchOnMountOrArgChange: true,
      refetchOnReconnect: true,
    }
  );
  useEffect(() => {
    if (!isLoading && data?.length) {
      const { key, value } = data[0];
      dispatch(loadConfig(parseDataTypeResult<GridConfigDictionary>(value)));
      dispatch(setGridConfigKey(key));
    }
    if (!data?.length) {
      dispatch(loadConfig({}));
    }
  }, [data, isLoading, dispatch]);

  return { data, isLoading, isFetching, isUninitialized };
}

export function usePersistGrid(id: WidgetID) {
  const dispatch = useDispatch();
  const gridConfig = useSelector(selectGridConfig);
  const configKey = useSelector(selectConfigKey);
  const currentGrid = useMemo(() => (gridConfig[id] ? gridConfig[id] : null), [gridConfig, id]);
  const [createGridConfig] = useCreateUserDataRowMutation();
  const [updateGridConfig] = useUpdateUserDataRowMutation();
  const storeConfig = useCallback(
    async (targetGrid: GridConfigDictionary) => {
      const nextConfig: GridConfigDictionary = {
        ...gridConfig,
        ...targetGrid,
      };
      const payload = toDataTypeResult<GridConfigDictionary>(nextConfig);
      if (configKey > -1) {
        await updateGridConfig({
          key: configKey,
          data: payload,
          userDataTypeId: UserDataType.GridConfiguration,
        });
      } else {
        const result = await createGridConfig({
          userDataTypeId: UserDataType.GridConfiguration,
          data: payload,
        });
        if (result?.data) {
          dispatch(setGridConfigKey(result.data[0]));
        }
      }
      dispatch(loadConfig(nextConfig));
    },
    [gridConfig, configKey, updateGridConfig, createGridConfig, dispatch]
  );

  const removeConfig = useCallback(async () => {
    const updatedConfig = { ...gridConfig };
    delete updatedConfig[id];
    await updateGridConfig({
      key: configKey,
      data: toDataTypeResult<GridConfigDictionary>(updatedConfig),
      userDataTypeId: UserDataType.GridConfiguration,
    });
  }, [gridConfig, configKey, updateGridConfig, id]);

  const removeAllConfig = useCallback(async () => {
    try {
      await updateGridConfig({
        key: configKey,
        data: toDataTypeResult<GridConfigDictionary>({}),
        userDataTypeId: UserDataType.GridConfiguration,
      });

      dispatch(
        showToast({
          id: 'remove-all-grid-config-success',
          text: 'All grid configurations have been successfully removed.',
          type: NotificationType.SUCCESS,
        })
      );

      dispatch(loadConfig({}));
    } catch (error) {
      if (error) {
        dispatch(
          showToast({
            id: 'remove-all-grid-config-error',
            text: 'An error occurred while removing all grid configurations.',
            type: NotificationType.ERROR,
          })
        );
      }
    }
  }, [configKey, updateGridConfig, dispatch]);

  return { gridConfig: currentGrid, storeConfig, removeConfig, removeAllConfig };
}
